import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import Seo from '../components/Seo';
import { Users, Shield, Handshake } from 'phosphor-react';

// Service Feature Card Component
const ServiceFeature = ({ title, description, benefits, icon: Icon }) => (
  <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 p-6 h-full">
    <div className="flex items-center mb-4">
      <div className="flex-shrink-0 mr-4">
        {Icon && <Icon size={32} weight="regular" className="text-terrapin-blue" />}
      </div>
      <h3 className="text-xl font-semibold text-terrapin-blue">{title}</h3>
    </div>
    <p className="text-gray-600 mb-6">{description}</p>

    {benefits && benefits.length > 0 && (
      <ul className="space-y-2">
        {benefits.map((benefit, index) => (
          <li key={index} className="flex items-start">
            <span className="text-terrapin-blue mr-2">✓</span>
            <span className="text-gray-700">{benefit}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

// Services Data
const servicesData = [
  {
    title: 'A Dedicated Tech Team',
    description: 'A simple, flat-fee engagement that covers your company\'s daily tech needs, from ongoing support to proactive system management and strategy.',
    benefits: [
      'No long-term contracts',
      'Remote support included',
      'High-level consulting for your business technology included'
    ],
    icon: Users
  },
  {
    title: 'Protection You Can Rely On',
    description: 'A proven stack of security and backup utilities that protects your systems, data, and devices without bloated tools or unnecessary complexity.',
    benefits: [
      'Encrypted cloud and local backups',
      'Managed antivirus and DNS filtering',
      'Business-grade firewall'
    ],
    icon: Shield
  },
  {
    title: 'A Real Relationship',
    description: 'You\'re not calling into a queue. We build long-term relationships with each company we support, learning your systems, your staff, and your priorities so we can respond like part of the team.',
    benefits: [
      'Named support team',
      'Hands-on onboarding',
      'Ongoing communication'
    ],
    icon: Handshake
  }
];

const Services = () => {
  return (
    <>
    <Seo
      title="Managed IT, Security & Helpdesk | Terrapin Networks"
      description="Explore Terrapin Networks' managed IT services: network management, cybersecurity, and helpdesk support for small businesses in Northern Michigan."
    />
    <PageLayout
      title="What We Do"
      description={
        <div className="max-w-3xl">
          <p className="text-xl mb-4">A Full Tech Team, Built for Small Businesses</p>
        </div>
      }
      variant="default"
    >
      <div className="prose prose-lg max-w-6xl mx-auto mb-12">
        <div className="prose prose-lg max-w-6xl mx-auto mb-12">
          <div className="mb-6">
            <img
              src={require('../assets/services.jpg')}
              alt="Services"
              className="hidden md:block md:float-left md:w-1/3 md:mr-6 mb-4 rounded-lg object-cover"
              loading="lazy"
            />
            <p className="text-lg mb-6">
              Most small businesses don't have an in-house tech department, and in many cases it wouldn't make sense to do so. We provide a tech team specifically designed for your company size and operations.
            </p>
            <p className="text-lg mb-6">
              At Terrapin Networks, you don't get a ticket number. You get a family-run business that learns your company, your staff, and the individual details that make your business tick. We're proactive, responsive, and designed to operate like we've got an office down the hall.
            </p>
            <p className="text-lg mb-10">
              We work exclusively with small companies - generally up to 25-30 computer users - which means you're never competing for our attention.
            </p>
            <div className="clear-both" />
          </div>
        </div>
        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-20">
          <a
            href="#copy-section"
            className="hidden md:block p-4 bg-white/10 backdrop-blur-sm rounded-full cursor-pointer hover:bg-white/20 transition-colors duration-200"
            onClick={(e) => {
              e.preventDefault();
              const element = document.getElementById('we-offer');
              const navbarHeight = 80; // adjust to match your navbar height
              const top = element.getBoundingClientRect().top + window.scrollY - navbarHeight;
              window.scrollTo({ top, behavior: 'smooth' });
            }}
          >
            <svg
              className="w-6 h-6 text-black animate-bounce"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </a>
        </div>
        <hr className="my-12 border-gray-200" />

        <h2 id="we-offer" className="text-4xl font-semibold text-terrapin-blue mt-6 mb-8 text-center">We Offer</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
        {servicesData.map((service, index) => (
          <ServiceFeature key={index} {...service} />
        ))}
      </div>
      <div className="mt-10 mb-6 text-center">
        <Link
          to="/contact"
          className="btn-base btn-primary"
        >
          Get Started
          <svg className="ml-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </Link>
      </div>
    </PageLayout>
    </>
  );
};

export default Services; 